import { dateLocale } from "@/lib/dateTime";
import React from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Briefcase, Calendar, ExternalLink, Eye, FileSignature } from "lucide-react";
import { formatINR } from "@/lib/utils";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import type { ProposalListRow } from "@/types/proposal";

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "—";

/** Proposals for a customer (account or contact), newest first */
export function ProposalsWidget({ proposals }: { proposals: ProposalListRow[] }) {
  const accepted = proposals.filter((p) => p.proposal_status === "ACCEPTED");
  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border/80">
        <div className="size-8 rounded-xl bg-primary/10 text-accent-foreground flex items-center justify-center shrink-0">
          <FileSignature className="size-4" />
        </div>
        <div>
          <h3 className="text-sm font-semibold tracking-tight text-foreground">Proposals ({proposals.length})</h3>
          <p className="text-[11px] text-muted-foreground">
            Solution documents sent to the customer{accepted.length ? ` · ${accepted.length} accepted` : ""}. Start one from a deal.
          </p>
        </div>
      </div>

      {proposals.length === 0 ? (
        <div className="p-8 text-center space-y-1">
          <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
            <FileSignature className="size-5" />
          </div>
          <p className="text-sm font-medium text-foreground">No proposals yet</p>
          <p className="text-xs text-muted-foreground">Proposals are written from a deal at the Qualification or Proposal stage.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="text-xs font-semibold">Proposal</TableHead>
                <TableHead className="text-xs font-semibold">Status</TableHead>
                <TableHead className="text-xs font-semibold">Indicative total</TableHead>
                <TableHead className="text-xs font-semibold">Deal</TableHead>
                <TableHead className="text-xs font-semibold">Sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.map((p) => (
                <TableRow key={p.id} className="hover:bg-muted/40 transition-colors">
                  <TableCell className="text-xs py-3">
                    <Link to={`/proposals/${p.id}`} className="font-semibold font-mono text-foreground hover:text-primary hover:underline inline-flex items-center gap-1 group">
                      {p.display_number}
                      <ExternalLink className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{p.title}</div>
                  </TableCell>
                  <TableCell className="text-xs py-3">
                    <div className="flex items-center gap-2">
                      <ProposalStatusBadge status={p.proposal_status} />
                      {p.view_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground" title={`Opened ${p.view_count} time(s)`}>
                          <Eye className="size-3" /> {p.view_count}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs py-3 font-semibold font-mono text-foreground">{formatINR(p.estimate_total)}</TableCell>
                  <TableCell className="text-xs py-3">
                    {p.deal ? (
                      <Link to={`/deals/${p.deal.id}`} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 truncate max-w-[180px]">
                        <Briefcase className="size-3 shrink-0" />
                        <span className="truncate">{p.deal.name}</span>
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground py-3">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="size-3 text-muted-foreground/80" />
                      {p.sent_at ? fmtDate(p.sent_at) : "Not sent"}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
